import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import PhoneNumberField, { PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import {
    BillPaymentFormRequest,
    CustomerData,
    PaymentData,
} from "@preferredMedical/Models/CustomForms/BillPayment/BillPaymentFormRequest";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { sendBillPayment } from "@preferredMedical/Services/CustomFormsService";
import { emailField, requiredStringField } from "@preferredMedical/Services/FormValidation";
import CardHelper, { CardData, CardHelperSettings } from "@preferredMedical/Widgets/SecureOnlineBillPaymentForm/CardHelper";
import parse from "html-react-parser";
import React, { ChangeEvent, Component } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface BillPaymentStyles {
    root?: GridContainerProps;
    rootItem?: GridItemProps;
    container?: GridContainerProps;
    sideContainer?: GridItemProps;
    subContainer?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    oneLinerGridItemNoPadding?: GridItemProps;
    paymentDetailsGridContainer?: GridContainerProps;
    submitButton?: ButtonPresentationProps;
    errorText?: TypographyProps;
}

export const billPaymentStyles: BillPaymentStyles = {
    root: { gap: 10 },
    rootItem: { width: 12 },
    container: { gap: 10 },
    sideContainer: {
        width: [12, 12, 12, 6, 6],
        css: css`
            margin-top: 20px;
        `,
    },
    subContainer: {
        gap: 10,
        css: css`
            margin-bottom: 20px;
        `,
    },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
        `,
    },
    oneLinerGridItemNoPadding: {
        width: 12,
        css: css`
            padding-left: 0;
        `,
    },
    paymentDetailsGridContainer: {
        gap: 9,
        css: css`
            padding-left: 10px;
            margin-top: 31px;
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
            margin-top: 116px;
        `,
    },
    errorText: {
        color: "danger",
        weight: 600,
        css: css`
            padding-left: 10px;
        `
    }
};

interface OwnProps {
    emailRecipients: string;
    introductoryText: string;
    maxTransactionAmount: string | number;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ customerNumber: string, invoiceNumbers: string, amount: number }>(location.search);
    const invoiceCustomerNumber = parsedQuery.customerNumber;
    const invoiceNumbers = parsedQuery.invoiceNumbers;
    const invoicesAmount = parsedQuery.amount;

    return {
        invoiceCustomerNumber,
        invoiceNumbers,
        invoicesAmount,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = billPaymentStyles;

interface CustomerErrors {
    companyName: string;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phoneNumber: string;
    email: string;
}

interface PaymentErrors {
    paymentMethod: string;
    invoiceNumber: string;
    customerNumber: string;
    amount: string;
}

interface State {
    customerData: CustomerData;
    customerErrors: CustomerErrors;

    paymentData: PaymentData;
    paymentErrors: PaymentErrors;

    getCardDataEvent: EventEx;
    tokenizeEvent: EventEx;
    validateTokenExEvent: EventEx;
    resetTokenExEvent: EventEx;
    cardHelperData: CardData;
    cardHelperSettings: CardHelperSettings;
    phoneNumberOnChangeEvent: EventEx;
    phoneNumberUpdateEvent: EventEx;

    formError: string;
    inProgress: boolean;
    disableInvoiceNumber: boolean;
    disableCustomerNumber: boolean;
}

class SecureOnlineBillPaymentForm extends Component<Props, State> {
    private static RequiredFieldMessage = "This is a required field.";
    private static EmailFormatFieldMessage = "Please enter a valid email address.";

    constructor(props: Props) {
        super(props);
        const prefilledInvoiceNumber = props.invoiceNumbers === undefined ? "" : props.invoiceNumbers;
        const prefilledCustomerNumber = props.invoiceCustomerNumber === undefined ? "" : props.invoiceCustomerNumber;
        const prefilledInvoicesAmount = props.invoicesAmount === undefined ? "" : props.invoicesAmount;
        const disableInvoiceNumber = props.invoiceNumbers !== undefined;
        const disableCustomerNumber = props.invoiceCustomerNumber !== undefined;
 
        this.state = {
            paymentData: {
                paymentMethod: "Credit Card",
                invoiceNumber: prefilledInvoiceNumber,
                customerNumber: prefilledCustomerNumber,
                amount: prefilledInvoicesAmount,
            },
            paymentErrors: {
                paymentMethod: "",
                invoiceNumber: "",
                customerNumber: "",
                amount: "",
            },
            customerData: {
                companyName: "",
                firstName: "",
                lastName: "",
                address: "",
                city: "",
                state: "",
                zip: "",
                country: "",
                phoneNumber: "",
                email: "",
            },
            customerErrors: {
                companyName: "",
                firstName: "",
                lastName: "",
                address: "",
                city: "",
                state: "",
                zip: "",
                country: "",
                phoneNumber: "",
                email: "",
            },
            cardHelperData: {
                cardHolderName: "",
                cardNumber: "",
                cardType: "",
                expirationMonth: new Date().getMonth(),
                expirationYear: new Date().getFullYear(),
                securityCode: "",
                possibleCardType: "",
            },
            cardHelperSettings: {
                canSaveCard: false,
                saveCard: false,
            },
            getCardDataEvent: new EventEx(),
            tokenizeEvent: new EventEx(),
            validateTokenExEvent: new EventEx(),
            resetTokenExEvent: new EventEx(),
            phoneNumberUpdateEvent: new EventEx(),
            phoneNumberOnChangeEvent: new EventEx(),
            formError: "",
            inProgress: false,
            disableInvoiceNumber,
            disableCustomerNumber,
        };

        this.bindFunctions();
        this.subscribeToEvents();
    }

    private isInvalidAmount(value: string) {
        const number = Number(value);
        const isNan = Number.isNaN(number);
        if (isNan) {
            return "Please enter a valid amount. Eg: 59.75";
        }
        if (number < 0.01) {
            return "Minimum amount is 0.01";
        }
        if (this.props.maxTransactionAmount && number > this.props.maxTransactionAmount) {
            return `Maximum amount is ${this.props.maxTransactionAmount}`;
        }
        return "";
    }

    private static getAmount(value: string) {
        return Number(value);
    }

    async componentDidMount() {}

    async submit() {
        if (this.state.inProgress) {
            return;
        }
        this.setState({ inProgress: true });
    
        const dataIsValid = await this.validateData();
        if (!dataIsValid) {
            this.setState({ inProgress: false });
            return;
        }
    
        if (this.state.paymentData.paymentMethod === "Credit Card") {
            await this.state.tokenizeEvent.emitAsync();
            const cardDataPromise = new Promise((resolve) => {
                this.state.getCardDataEvent.subscribe((cardData: CardData) => {
                    this.setState({ cardHelperData: cardData }, () => resolve(cardData));
                });
            });
    
            const cardData = await cardDataPromise;
            const model = this.buildModel();
            try {
                await sendBillPayment(model);
                const confirmationPageUrl = `${window.location.href.split('?')[0]}/Confirmation`;
                window.location.href = confirmationPageUrl;
            } catch (error) {
                await this.state.resetTokenExEvent.emitAsync();
                this.setState({
                    formError: translate("Something went wrong. Please try again."),
                    inProgress: false,
                });
            }
        }
    }

    async validateData() {
        await this.state.validateTokenExEvent.emitAsync();
        const isCompanyNameValid = this.validateCustomerField("companyName", this.state.customerData.companyName);
        const isFirstNameValid = this.validateCustomerField("firstName", this.state.customerData.firstName);
        const isLastNameValid = this.validateCustomerField("lastName", this.state.customerData.lastName);
        const isAddressValid = this.validateCustomerField("address", this.state.customerData.address);
        const isCityValid = this.validateCustomerField("city", this.state.customerData.city);
        const isStateValid = this.validateCustomerField("state", this.state.customerData.state);
        const isZipValid = this.validateCustomerField("zip", this.state.customerData.zip);
        const isCountryValid = this.validateCustomerField("country", this.state.customerData.country);
        const isEmailValid = this.validateEmail(this.state.customerData.email);

        await this.state.phoneNumberUpdateEvent.emitAsync();
        const isPhoneNumberValid = this.state.customerErrors.phoneNumber === "";

        const isPaymentMethodValid = this.validatePaymentMethod(this.state.paymentData.paymentMethod);
        const isInvoiceNumberValid = this.validatePaymentField("invoiceNumber", this.state.paymentData.invoiceNumber);
        const isCustomerNumberValid = this.validatePaymentField(
            "customerNumber",
            this.state.paymentData.customerNumber,
        );
        const isAmountValid = this.validateAmount(this.state.paymentData.amount.toString());

        return (
            isCompanyNameValid &&
            isFirstNameValid &&
            isLastNameValid &&
            isAddressValid &&
            isCityValid &&
            isStateValid &&
            isZipValid &&
            isCountryValid &&
            isEmailValid &&
            isPhoneNumberValid &&
            isPaymentMethodValid &&
            isInvoiceNumberValid &&
            isCustomerNumberValid &&
            isAmountValid
        );
    }

    async tokenizeAndSendRequest() {
        await this.state.tokenizeEvent.emitAsync();
    }

    render() {
        return (
            <GridContainer {...styles.root}>
                <GridItem {...styles.rootItem}>
                    <Typography>
                        {parse(this.props.introductoryText ? this.props.introductoryText : "", parserOptions)}
                    </Typography>
                </GridItem>
                <GridItem {...styles.rootItem}>
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.sideContainer}>
                            <GridContainer {...styles.subContainer}>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <Typography variant="h5">Customer Details</Typography>
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="Company Name"
                                        name="companyName"
                                        value={this.state.customerData.companyName}
                                        onChange={this.handleCustomerFieldChange}
                                        error={this.state.customerErrors.companyName}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="First Name"
                                        name="firstName"
                                        value={this.state.customerData.firstName}
                                        onChange={this.handleCustomerFieldChange}
                                        error={this.state.customerErrors.firstName}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="Last Name"
                                        name="lastName"
                                        value={this.state.customerData.lastName}
                                        onChange={this.handleCustomerFieldChange}
                                        error={this.state.customerErrors.lastName}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="Address"
                                        name="address"
                                        value={this.state.customerData.address}
                                        onChange={this.handleCustomerFieldChange}
                                        error={this.state.customerErrors.address}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="City"
                                        name="city"
                                        value={this.state.customerData.city}
                                        onChange={this.handleCustomerFieldChange}
                                        error={this.state.customerErrors.city}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="State/Province"
                                        name="state"
                                        value={this.state.customerData.state}
                                        onChange={this.handleCustomerFieldChange}
                                        error={this.state.customerErrors.state}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="Zip/Postal Code"
                                        name="zip"
                                        value={this.state.customerData.zip}
                                        onChange={this.handleCustomerFieldChange}
                                        error={this.state.customerErrors.zip}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="Country"
                                        name="country"
                                        value={this.state.customerData.country}
                                        onChange={this.handleCustomerFieldChange}
                                        error={this.state.customerErrors.country}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <PhoneNumberField
                                        label="Phone Number"
                                        format="000-000-0000"
                                        initialValue={this.state.customerData.phoneNumber}
                                        required={true}
                                        onChange={this.state.phoneNumberOnChangeEvent}
                                        update={this.state.phoneNumberUpdateEvent}
                                        hideRequiredAsterisk={true}
                                    />
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <TextField
                                        label="Email"
                                        value={this.state.customerData.email}
                                        onChange={this.handleEmailChange}
                                        error={this.state.customerErrors.email}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.sideContainer}>
                            <GridContainer {...styles.subContainer}>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <GridContainer {...styles.subContainer}>
                                        <CardHelper
                                            initialData={this.state.cardHelperData}
                                            settings={this.state.cardHelperSettings}
                                            getDataEvent={this.state.getCardDataEvent}
                                            triggerValidateEvent={this.state.validateTokenExEvent}
                                            submitEvent={this.state.tokenizeEvent}
                                            resetTokenExEvent={this.state.resetTokenExEvent}
                                        />
                                    </GridContainer>
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItemNoPadding}>
                                    <GridContainer {...styles.paymentDetailsGridContainer}>
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <TextField
                                                label="Invoice #"
                                                name="invoiceNumber"
                                                value={this.state.paymentData.invoiceNumber}
                                                disabled={this.state.disableInvoiceNumber}
                                                onChange={this.handlePaymentChange}
                                                error={this.state.paymentErrors.invoiceNumber}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <TextField
                                                label="Customer #"
                                                name="customerNumber"
                                                value={this.state.paymentData.customerNumber}
                                                disabled={this.state.disableCustomerNumber}
                                                onChange={this.handlePaymentChange}
                                                error={this.state.paymentErrors.customerNumber}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <TextField
                                                label="Amount"
                                                name="invoiceNumber"
                                                value={this.state.paymentData.amount}
                                                onChange={this.handleAmountChange}
                                                error={this.state.paymentErrors.amount}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <GridContainer {...styles.subContainer}>
                                        <GridItem {...styles.oneLinerGridItem}>
                                            <Button
                                                {...styles.submitButton}
                                                onClick={this.submit}
                                                disabled={this.state.inProgress}
                                            >
                                                {translate("Submit")}
                                            </Button>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                {this.state.formError && <GridItem {...styles.oneLinerGridItem}>
                                    <Typography {...styles.errorText}>{this.state.formError}</Typography>
                                </GridItem>}
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        );
    }

    private subscribeToEvents() {
        this.state.getCardDataEvent.subscribe((cardData: CardData) => {
            this.setState({ cardHelperData: cardData });
        });
    
        this.state.phoneNumberOnChangeEvent.subscribe((data: PhoneNumberState) => {
            this.setState({
                customerData: { ...this.state.customerData, phoneNumber: data.value },
                customerErrors: { ...this.state.customerErrors, phoneNumber: data.error },
            });
        });
    }    

    private buildModel() {
        const model: BillPaymentFormRequest = {
            customerData: this.state.customerData,
            paymentData: this.state.paymentData,
            creditCard: {
                cardHolderName: this.state.cardHelperData.cardHolderName,
                cardNumber: this.state.cardHelperData.cardNumber,
                cardType: this.state.cardHelperData.cardType,
                expirationMonth: this.state.cardHelperData.expirationMonth,
                expirationYear: this.state.cardHelperData.expirationYear,
                securityCode: this.state.cardHelperData.securityCode,
                address1: this.state.customerData.address,
                city: this.state.customerData.city,
                country: this.state.customerData.country,
                postalCode: this.state.customerData.zip,
                maskedCardNumber: "**** **** **** 1234",
                state: this.state.customerData.state,
                countryAbbreviation: this.state.customerData.country,
                stateAbbreviation: this.state.customerData.state,
                useBillingAddress: false,
            },
            emailTos: this.props.emailRecipients.split("\n"),
        };
        return model;
    }

    private bindFunctions() {
        this.submit = this.submit.bind(this);
        this.validateData = this.validateData.bind(this);
        this.buildModel = this.buildModel.bind(this);
        this.tokenizeAndSendRequest = this.tokenizeAndSendRequest.bind(this);
        this.buildModel = this.buildModel.bind(this);
        this.onPaymentMethodChange = this.onPaymentMethodChange.bind(this);
        this.handleCustomerFieldChange = this.handleCustomerFieldChange.bind(this);
        this.handlePaymentChange = this.handlePaymentChange.bind(this);
        this.handleEmailChange = this.handleEmailChange.bind(this);
        this.handleAmountChange = this.handleAmountChange.bind(this);
        this.validatePaymentMethod = this.validatePaymentMethod.bind(this);
        this.validateCustomerField = this.validateCustomerField.bind(this);
        this.validateEmail = this.validateEmail.bind(this);
        this.validatePaymentField = this.validatePaymentField.bind(this);
        this.validateAmount = this.validateAmount.bind(this);
    }

    private handleCustomerFieldChange(event: ChangeEvent<HTMLInputElement>) {
        event.persist();
        const value = event.target.value;
        const name = event.target.name;
        this.setState({ customerData: { ...this.state.customerData, [name]: value } });
        this.validateCustomerField(name, value);
    }

    private validateCustomerField(name: string, value: string) {
        const required = requiredStringField(value, SecureOnlineBillPaymentForm.RequiredFieldMessage, false);
        this.setState({ customerErrors: { ...this.state.customerErrors, [name]: required.errorMessage } });
        return !required.hasError;
    }

    private handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        this.setState({ customerData: { ...this.state.customerData, email: value } });
        this.validateEmail(value);
    }

    private validateEmail(value: string) {
        const required = requiredStringField(value, SecureOnlineBillPaymentForm.RequiredFieldMessage, false);
        if (required.hasError) {
            this.setState({ customerErrors: { ...this.state.customerErrors, email: required.errorMessage } });
            return !required.hasError;
        }
        const emailFormat = emailField(value, SecureOnlineBillPaymentForm.EmailFormatFieldMessage, false);
        this.setState({ customerErrors: { ...this.state.customerErrors, email: emailFormat.errorMessage } });
        return !emailFormat.hasError;
    }

    private handlePaymentChange(event: ChangeEvent<HTMLInputElement>) {
        event.persist();
        const value = event.target.value;
        const name = event.target.name;
        this.setState({ paymentData: { ...this.state.paymentData, [name]: value } });
        this.validatePaymentField(name, value);
    }

    private validatePaymentField(name: string, value: string) {
        const required = requiredStringField(value, SecureOnlineBillPaymentForm.RequiredFieldMessage, false);
        this.setState({ paymentErrors: { ...this.state.paymentErrors, [name]: required.errorMessage } });
        return !required.hasError;
    }

    private handleAmountChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        this.setState({ paymentData: { ...this.state.paymentData, amount: value } });
        this.validateAmount(value);
    }

    private validateAmount(value: string) {
        const required = requiredStringField(value, SecureOnlineBillPaymentForm.RequiredFieldMessage, false);
        this.setState({ paymentErrors: { ...this.state.paymentErrors, amount: required.errorMessage } });
        if (required.hasError) {
            return false;
        }
        const invalidAmountMessage = this.isInvalidAmount(value);
        if (invalidAmountMessage) {
            this.setState({
                paymentErrors: { ...this.state.paymentErrors, amount: invalidAmountMessage },
            });
            return false;
        }
        return true;
    }

    onPaymentMethodChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.currentTarget.value;
        this.setState({ paymentData: { ...this.state.paymentData, paymentMethod: value } });
        this.validatePaymentMethod(value);
    }

    private validatePaymentMethod(value: string) {
        if (value !== "Credit Card") {
            this.setState({
                paymentErrors: { ...this.state.paymentErrors, paymentMethod: "Please select a valid payment method." },
            });
            return false;
        }
        return true;
    }
}

export default connect(mapStateToProps)(SecureOnlineBillPaymentForm);
